import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { toast } from 'react-toastify';

import { phone } from 'phone'
import zipcodes from 'zipcodes'

// amplify
import { Auth, API, Storage } from 'aws-amplify'

// graphql
import { createOrganization, createAddress, createStaffProfile, updateAuthAccount, updateOrganization, createLocation } from '../../graphql/mutations'
import { getOrganizationInvitationByCode, listStaffProfilesByOrganization, getOrganization } from '../../graphql/queries'

// redux
import { reloadAuthAccount, setSelectedStaffProfile } from './authAccount'
import { setLoading } from './loading'
import { StaffProfile, Organization } from '../../../API'

import { connect } from 'getstream'

interface OrganizationState {
    data: object | null | undefined,
    createOrganizationInvitation: object | any | null,
    staffProfiles: StaffProfile[]
    organizationHeaderImage: string | null,
    activeClassyConnection: any[] | null
}

interface CreateOrganizationProps {
    name: string,
    shortName?: string,
    address: CreateAddressProps,
    website: string,
    phoneNumber: string,
}

interface CreateAddressProps {
    streetAddress: string,
    city: string,
    zip: number
}

interface IConfirmAdminAccount {
    code: string
}

const initialState: OrganizationState = {
    data: null,
    createOrganizationInvitation: null,
    staffProfiles: [],
    organizationHeaderImage: null,
    activeClassyConnection: null
}

export const createFlourishOrganization: any = createAsyncThunk(
    'organization/createFlourishOrganization',
    async (props: CreateOrganizationProps, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Creating your organization. This could take a few moments.'
            }))

            const normalizedPhoneNumber = phone(props.phoneNumber, { country: 'USA' })
            if (normalizedPhoneNumber.isValid) {

                await dispatch(reloadAuthAccount())
                const { authAccount }: any = getState()

                const createAddressData: any = await API.graphql({
                    query: createAddress,
                    variables: {
                        input: {
                            streetAddress: props.address.streetAddress,
                            city: props.address.city,
                            zip: props.address.zip,
                            state: zipcodes.lookup(props.address.zip)?.state,
                            longitude: zipcodes.lookup(props.address.zip)?.longitude,
                            latitude: zipcodes.lookup(props.address.zip)?.latitude
                        }
                    }
                })

                console.log('create address response: ', createAddressData.data)

                const createOrganizationData: any = await API.graphql({
                    query: createOrganization,
                    variables: {
                        input: {
                            name: props.name,
                            website: props.website,
                            phoneNumber: normalizedPhoneNumber.phoneNumber
                        }
                    }
                })

                console.log('create organization response: ', createOrganizationData.data)

                const createOrganizationLocationResponse: any = await API.graphql({
                    query: createLocation,
                    variables: {
                        input: {
                            type: 'physical',
                            addressID: createAddressData.data.createAddress.id,
                            organizationID: createOrganizationData.data.createOrganization.id,
                            name: createOrganizationData.data.createOrganization.name
                        }
                    }
                })

                console.log('create organization location response: ', createOrganizationLocationResponse.data)

                const updateOrganizationResponse: any = await API.graphql({
                    query: updateOrganization,
                    variables: {
                        input: {
                            id: createOrganizationData.data.createOrganization.id,
                            locationID: createOrganizationLocationResponse.data.createLocation.id
                        }
                    }
                })

                console.log('update organization response: ', updateOrganizationResponse.data)

                const createStaffProfileResponse: any = await API.graphql({
                    query: createStaffProfile,
                    variables: {
                        input: {
                            authAccountID: authAccount.authAccountId,
                            organizationID: createOrganizationData.data.createOrganization.id,
                            role: 'admin'
                        }
                    }
                })

                console.log('create staff profile response: ', createStaffProfileResponse.data)

                // get stream jwt

                const user = await Auth.currentAuthenticatedUser({ bypassCache: false })

                const apiName = 'RESTfundraising';
                const path = '/stream';

                const response: any = await API.get(apiName, path, { "queryStringParameters": { "jwt": user.signInUserSession.idToken.jwtToken, organizationID: createOrganizationData.data.createOrganization.id } });

                console.log('stream jwt response: ', response)

                const streamKey = response.token

                console.log(streamKey)

                if (streamKey) {
                    const updateOrganizationResponse: any = await API.graphql({
                        query: updateOrganization,
                        variables: {
                            input: {
                                id: createOrganizationData.data.createOrganization.id,
                                streamJwt: streamKey
                            }
                        }
                    })

                    const timeline = connect('ax9qpj459r9x', streamKey, '1170890', { location: 'us-east' })

                    const response = await timeline.feed('latent_interactions').addActivity({
                        actor: timeline.currentUser ? timeline.currentUser : '',
                        verb: 'post',
                        object: `This is the beginning of the interactions feed. Interactions show up here in real-time between users and Campaigns so you can see when any ambassador adds, texts, or calls a contact. You'll also be able to see when a contact donates to a Campaign. Let's get started!`,
                        campaignName: 'Welcome to Latent!'
                    })

                }

                const getOrganizationDataResponse: any = await API.graphql({
                    query: getOrganization,
                    variables: {
                        id: createOrganizationData.data.createOrganization.id
                    }
                })

                console.log('get organization data response: ', getOrganizationDataResponse.data)

                if (createAddressData.data.createAddress && createOrganizationData.data.createOrganization && createStaffProfileResponse.data.createStaffProfile && getOrganizationDataResponse.data.getOrganization) {
                    await dispatch(setSelectedStaffProfile(createStaffProfileResponse.data.createStaffProfile))

                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))

                    return {
                        staffProfile: createStaffProfileResponse.data.createStaffProfile,
                        organizationData: getOrganizationDataResponse.data.getOrganization
                    }
                }
            } else {
                alert('Phone number is not valid.')
                dispatch(setLoading({
                    loading: false,
                    message: 'Loading.'
                }))
                return {
                    staffProfile: null,
                    organizationData: null
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const getCreateOrganizationInvitation: any = createAsyncThunk(
    'organization/getCreateOrganizationInvitation',
    async (code: string, { dispatch, getState }) => {
        try {
            const { data }: any = await API.graphql({
                query: getOrganizationInvitationByCode,
                variables: {
                    code: code
                }
            })

            if (data.getOrganizationInvitationByCode) {
                console.log(data.getOrganizationInvitationByCode.items[0])
                return data.getOrganizationInvitationByCode.items[0]
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const loadOrganizationData: any = createAsyncThunk(
    'organization/loadOrganizationData',
    async (props: any, { dispatch, getState }) => {
        try {


            const { authAccount }: any = getState()

            if (authAccount.authAccountId) {
                const staffProfileResponse = await API.graphql({
                    query:
                        `
                    getAuthAccount(id: $authAccountId) {
                        staffProfile {
                          items {
                            organizationID
                          }
                        }
                      }
                    `,
                    variables: {
                        authAccountId: authAccount.authAccountId
                    }
                })

                console.log(staffProfileResponse)
            }

        } catch (err) {
            console.error(err)
        }
    }
)

export const getOrganizationStaffProfiles: any = createAsyncThunk(
    'organization/getOrganizationStaffProfiles',
    async (props: any, { dispatch, getState }) => {
        try {

            const { authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: listStaffProfilesByOrganization,
                variables: {
                    organizationID: authAccount.selectedStaffProfile.organizationID
                }
            })

            if (data.listStaffProfilesByOrganization) {
                return data.listStaffProfilesByOrganization.items
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const updateHeaderImage: any = createAsyncThunk(
    'organization/updateHeaderImage',
    async (path: string, { dispatch, getState }) => {
        try {

            const { authAccount }: any = getState()

            if (authAccount.selectedStaffProfile && authAccount.selectedStaffProfile.organizationID) {
                const { data }: any = await API.graphql({
                    query: updateOrganization,
                    variables: {
                        input: {
                            id: authAccount.selectedStaffProfile.organizationID,
                            headerImageURI: path,
                        }
                    }
                })

                const updatedOrganization = data.updateOrganization

                toast.success('Image uploaded.', {
                    position: "bottom-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });

                return updatedOrganization
            } else {
                return null
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const getOrganizationHeaderImage: any = createAsyncThunk(
    'organization/getOrganizationHeaderImage',
    async (props: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Loading organization image...'
            }))

            const { authAccount }: any = getState()

            if (authAccount && authAccount.selectedStaffProfile && authAccount.selectedStaffProfile.organizationID) {
                const signedURL = await Storage.get(`organizations/${authAccount.selectedStaffProfile.organizationID}/headerImage`)

                dispatch(setLoading({
                    loading: false,
                    message: 'Loading.'
                }))

                return {
                    organizationHeaderImageURI: signedURL
                }
            } else {
                return {
                    organizationHeaderImageURI: ''
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const getActiveClassyConnections: any = createAsyncThunk(
    'organization/getActiveClassyConnections',
    async (props: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Loading Classy integration settings...'
            }))

            const { authAccount }: any = getState()

            const user = await Auth.currentAuthenticatedUser({ bypassCache: false })

            if (authAccount.selectedStaffProfile) {
                const response = await API.get('RESTfundraising', '/latent/integrations/classy', {
                    queryStringParameters: {
                        jwt: user.signInUserSession.idToken.jwtToken,
                        organizationID: authAccount.selectedStaffProfile.organizationID
                    }
                })

                if (response && response.activeConnections) {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    return {
                        activeConnections: response.activeConnections
                    }
                } else {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    return {
                        activeConnections: []
                    }
                }
            }

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const createClassyConnection: any = createAsyncThunk(
    'organization/createClassyConnection',
    async (props: { apiClientID: string, apiClientSecret: string, classyOrganizationID: string }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Creating new Classy connection...'
            }))

            const { authAccount }: any = getState()

            const user = await Auth.currentAuthenticatedUser({ bypassCache: false })

            if (authAccount.selectedStaffProfile) {
                const response = await API.post('RESTfundraising', '/latent/integrations/classy', {
                    body: {
                        jwt: user.signInUserSession.idToken.jwtToken,
                        organizationID: authAccount.selectedStaffProfile.organizationID,
                        apiClientID: props.apiClientID,
                        apiClientSecret: props.apiClientSecret,
                        classyOrganizationID: props.classyOrganizationID
                    }
                })

                if (response && response.activeConnections) {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    return {
                        activeConnections: response.activeConnections
                    }
                } else {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    return {
                        activeConnections: []
                    }
                }
            }

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const organizationSlice = createSlice({
    name: 'organization',
    initialState,
    reducers: {
        clearLocalUserData: (state) => {
            state.data = null
        },
        setOrganizationStaffProfiles: (state, action) => {
            state.staffProfiles = action.payload
        },
        addStaffProfile: (state, action) => {
            state.staffProfiles.push(action.payload)
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createFlourishOrganization.fulfilled, (state, { payload }) => {
            if (payload.organizationData) {
                state.data = payload.organizationData
            } else {
                console.error('Error creating organization. This could be for a number of reasons, possibly because the entered phone number was invalid or one of the http requests was not successful.')
            }
        })
        builder.addCase(getCreateOrganizationInvitation.fulfilled, (state, { payload }) => {
            state.createOrganizationInvitation = payload
        })
        builder.addCase(getOrganizationStaffProfiles.fulfilled, (state, { payload }) => {
            state.staffProfiles = payload
        })
        builder.addCase(getOrganizationHeaderImage.fulfilled, (state, { payload }) => {
            state.organizationHeaderImage = payload.organizationHeaderImageURI
        })
        builder.addCase(getActiveClassyConnections.fulfilled, (state, { payload }) => {
            console.log(payload)
            if (payload.activeConnections) {
                state.activeClassyConnection = payload.activeConnections
            }
        })
        builder.addCase(createClassyConnection.fulfilled, (state, { payload }) => {
            console.log(payload)
            if (payload.activeConnections) {
                state.activeClassyConnection = payload.activeConnections
            }
        })
    }
})

export const { clearLocalUserData, setOrganizationStaffProfiles, addStaffProfile } = organizationSlice.actions
export default organizationSlice.reducer